import { PERMISSIONS } from '@ibe/constants';
import {
  Button,
  ConfirmDialog,
  DataTable,
  Pagination,
  SearchInput,
  StatusBadge,
  toneForStatus,
} from '@ibe/ui';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ColumnPicker } from '../../../components/resource-list/column-picker.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import {
  resolveVisibleColumns,
  type ColumnDescriptor,
} from '../../../lib/resource-list/columns.js';
import { useAppStore } from '../../../lib/state/store.js';
import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { useSoftDeletePromotion } from '../api/mutations.js';
import { usePromotions } from '../api/queries.js';
import { type PromotionRow } from '../api/schemas.js';

const DEFAULT_SORT = { columnId: 'createdAt', direction: 'desc' } as const;
const PAGE_SIZE = 10;
const FIRST_PAGE = 1;

const COLUMNS: readonly ColumnDescriptor<PromotionRow>[] = [
  {
    id: 'code',
    header: 'Code',
    sortable: true,
    alwaysVisible: true,
    fields: ['code', 'name'],
    cell: (row) => (
      <PrimaryCell
        label={row.code ?? row.id}
        to={`/promotions/${row.id}`}
        secondary={row.name ?? null}
      />
    ),
  },
  {
    id: 'name',
    header: 'Name',
    sortable: true,
    fields: ['name'],
    cell: (row) => row.name,
  },
  {
    id: 'type',
    header: 'Type',
    sortable: true,
    fields: ['type'],
    cell: (row) => row.type,
  },
  {
    id: 'priority',
    header: 'Priority',
    sortable: true,
    fields: ['priority'],
    cell: (row) => (row.priority === undefined ? null : String(row.priority)),
  },
  {
    id: 'enabled',
    header: 'Status',
    sortable: true,
    fields: ['enabled'],
    cell: (row) =>
      row.enabled === undefined ? null : (
        <StatusBadge tone={toneForStatus(row.enabled ? 'ACTIVE' : 'INACTIVE')}>
          {row.enabled ? 'Active' : 'Inactive'}
        </StatusBadge>
      ),
  },
  {
    id: 'platform',
    header: 'Platform',
    sortable: true,
    fields: ['platform'],
    cell: (row) => row.platform,
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['code'],
    cell: (row: PromotionRow) => <PromotionRowActions row={row} />,
  },
] as const;

const RESOURCE = 'promotions';

function PromotionRowActions({ row }: { readonly row: PromotionRow }): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = useSoftDeletePromotion();
  const label = row.name ?? row.code ?? row.id;

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            { label: `View ${label}`, icon: Eye, to: `/promotions/${row.id}` },
            { label: `Edit ${label}`, icon: Pencil, to: `/promotions/${row.id}/edit` },
          ]}
        />
        <PermissionGuard permission={PERMISSIONS.PROMOTION_DELETE}>
          <RowActions
            actions={[
              {
                label: `Delete ${label}`,
                icon: Trash2,
                destructive: true,
                onClick: () => {
                  setConfirming(true);
                },
              },
            ]}
          />
        </PermissionGuard>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete promotion"
        description={`${label} will be removed. Its audit history and any bookings that used it are retained.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(row.id, {
            onSuccess: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}

function filterRows(rows: readonly PromotionRow[], q: string): readonly PromotionRow[] {
  const needle = q.trim().toLowerCase();
  if (needle === '') return rows;
  return rows.filter(
    (row) =>
      (row.name ?? '').toLowerCase().includes(needle) ||
      (row.code ?? '').toLowerCase().includes(needle) ||
      (row.type ?? '').toLowerCase().includes(needle),
  );
}

function sortRows(
  rows: readonly PromotionRow[],
  sortBy: string,
  sortDirection: 'asc' | 'desc',
): readonly PromotionRow[] {
  const sorted = [...rows];
  const direction = sortDirection === 'asc' ? 1 : -1;

  sorted.sort((left, right) => {
    const leftValue = left[sortBy as keyof PromotionRow];
    const rightValue = right[sortBy as keyof PromotionRow];

    if (leftValue === rightValue) return 0;
    if (leftValue === undefined || leftValue === null) return direction;
    if (rightValue === undefined || rightValue === null) return -direction;

    if (typeof leftValue === 'string' && typeof rightValue === 'string') {
      return leftValue.localeCompare(rightValue) * direction;
    }

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return (leftValue - rightValue) * direction;
    }

    if (typeof leftValue === 'boolean' && typeof rightValue === 'boolean') {
      return (Number(leftValue) - Number(rightValue)) * direction;
    }

    return 0;
  });

  return sorted;
}

export function PromotionListPage(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const sortBy = params.get('sortBy') ?? DEFAULT_SORT.columnId;
  const sortDirection = (params.get('sortDirection') ?? DEFAULT_SORT.direction) as 'asc' | 'desc';

  const columnSelection = useAppStore((state) => state.prefs.columns[RESOURCE]);

  const page = Math.max(
    FIRST_PAGE,
    Number.isNaN(Number(params.get('page'))) ? FIRST_PAGE : Number(params.get('page')),
  );

  const query = usePromotions();

  const rows = useMemo(() => {
    const filtered = filterRows(query.data ?? [], q);
    return sortRows(filtered, sortBy, sortDirection);
  }, [query.data, q, sortBy, sortDirection]);

  const visibleColumns = useMemo(
    () => resolveVisibleColumns(COLUMNS, columnSelection),
    [columnSelection],
  );

  const totalItems = rows.length;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE) || 1;
  const safePage = Math.min(page, totalPages);
  const pagedRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, safePage]);

  const meta = useMemo(
    () => ({
      page: safePage,
      pageSize: PAGE_SIZE,
      totalItems,
      totalPages,
      hasNextPage: safePage < totalPages,
      hasPreviousPage: safePage > FIRST_PAGE,
    }),
    [safePage, totalItems, totalPages],
  );

  const update = (changes: Readonly<Record<string, string | undefined>>): void => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined || value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  return (
    <PermissionGuard permission={PERMISSIONS.PROMOTION_READ} fallback={<NotPermitted />}>
      <section className="space-y-section">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-xl font-semibold">Promotions</h1>
            <p className="text-sm text-muted-foreground">
              Discounts, stay-and-pay offers and fixed-rate overrides.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ColumnPicker columns={COLUMNS} resource={RESOURCE} />
            <PermissionGuard permission={PERMISSIONS.PROMOTION_CREATE}>
              <Button asChild>
                <Link to="/promotions/new">
                  <Plus className="mr-2 h-4 w-4" />
                  New promotion
                </Link>
              </Button>
            </PermissionGuard>
          </div>
        </header>

        <SearchInput
          label="Search promotions"
          defaultValue={q}
          onSearch={(term) => {
            update({ q: term, page: undefined });
          }}
        />

        <DataTable
          caption="Promotions"
          columns={visibleColumns}
          rows={pagedRows}
          rowKey={(row) => row.id}
          loading={query.isPending}
          error={messageFor(query.error)}
          onRetry={() => {
            void query.refetch();
          }}
          sort={{ columnId: sortBy, direction: sortDirection }}
          onSortChange={(next) => {
            update({ sortBy: next.columnId, sortDirection: next.direction, page: undefined });
          }}
          emptyTitle="No promotions found"
          emptyDescription="There are no promotions for this tenant. Create one to get started."
        />

        {query.data === undefined ? null : (
          <Pagination
            meta={meta}
            disabled={query.isFetching}
            onPageChange={(next) => {
              update({ page: String(next) });
            }}
          />
        )}
      </section>
    </PermissionGuard>
  );
}

function messageFor(error: unknown): string | undefined {
  if (error === null || error === undefined) return undefined;
  if (error instanceof Error) return error.message;
  return 'Something went wrong loading the promotions.';
}
